import React, { createContext, useState, useEffect } from 'react';
import axios from 'axios';
import url from '../url.js';

export const AuthContext = createContext();

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    checkAuth();
  }, []);

  const checkAuth = async () => {
    try {
      const response = await axios.get(`${url.backendHost}api/users/renew`, {
        withCredentials: true
      });
      if (response.data) {
        setUser(response.data);
        localStorage.setItem('userId', response.data.id);
        localStorage.setItem('userName', response.data.name);
        localStorage.setItem('userEmail', response.data.email);
        
        // 存储更新的 JWT token
        if (response.data.jwtToken) {
          localStorage.setItem('jwtToken', response.data.jwtToken);
        }
      }
    } catch (error) {
      console.error('Auth check failed:', error);
      setUser(null);
      localStorage.removeItem('userId');
      localStorage.removeItem('userName');
      localStorage.removeItem('userEmail');
      localStorage.removeItem('jwtToken');
    } finally {
      setLoading(false);
    }
  };

  const login = async (email, password) => {
    try {
      const response = await axios.post(`${url.backendHost}api/users/login`, {
        email,
        password
      }, {
        withCredentials: true
      });
      setUser(response.data);
      localStorage.setItem('userId', response.data.id);
      localStorage.setItem('userName', response.data.name);
      localStorage.setItem('userEmail', response.data.email);
      
      // 存储 JWT token
      if (response.data.jwtToken) {
        localStorage.setItem('jwtToken', response.data.jwtToken);
        console.log('JWT token 已存储:', response.data.jwtToken);
      } else {
        console.warn('登录响应中未找到 jwtToken');
      }
      
      return response.data;
    } catch (error) {
      throw error;
    }
  };

  const logout = async () => {
    // First, immediately clear client state to prevent race conditions
    setUser(null);
    
    // Clear all user-related localStorage and sessionStorage items
    const clearLocalStorage = () => {
      // Clear localStorage
      localStorage.removeItem('userId');
      localStorage.removeItem('userName');
      localStorage.removeItem('userEmail');
      localStorage.removeItem('name');
      localStorage.removeItem('email');
      localStorage.removeItem('activityId');
      localStorage.removeItem('groupId');
      localStorage.removeItem('nodeId');
      localStorage.removeItem('nodeDataLength');
      localStorage.removeItem('jwtToken');
      
      // Clear sessionStorage completely
      sessionStorage.clear();
    };
    
    // Clear all cookies from client side
    const clearAllCookies = () => {
      const cookies = document.cookie.split(";");
      
      for (let cookie of cookies) {
        const eqPos = cookie.indexOf("=");
        const name = eqPos > -1 ? cookie.substr(0, eqPos).trim() : cookie.trim();
        
        // Clear cookie for current domain
        document.cookie = `${name}=; expires=Thu, 01 Jan 1970 00:00:00 GMT; path=/;`;
        
        // Clear cookie for parent domain (if subdomain)
        const domain = window.location.hostname;
        if (domain.includes('.')) {
          const parentDomain = '.' + domain.split('.').slice(-2).join('.');
          document.cookie = `${name}=; expires=Thu, 01 Jan 1970 00:00:00 GMT; path=/; domain=${parentDomain};`;
        }
        
        // Clear for localhost specifically
        document.cookie = `${name}=; expires=Thu, 01 Jan 1970 00:00:00 GMT; path=/; domain=localhost;`;
      }
    };
    
    clearLocalStorage();
    clearAllCookies();
    
    try {
      // Try to logout from server
      await axios.get(`${url.backendHost}auth/logout`, {
        withCredentials: true
      });
    } catch (error) {
      console.error('Logout failed:', error);
      // Even if server logout fails, we've already cleared client state
    }
  };

  return (
    <AuthContext.Provider value={{ user, loading, login, logout, checkAuth }}>
      {children}
    </AuthContext.Provider>
  );
}; 